import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Handles scroll position on route changes:
 * - PUSH (clicking a link): saves the current scroll position for this page,
 *   then scrolls to top of the new page.
 * - POP (back/forward button or navigate(-1)): restores the saved scroll
 *   position for the page being returned to, so the user lands back where
 *   they were in a shop listing rather than at the top.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const prevPathnameRef = useRef(pathname);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Meta pixel PageViews on route change are handled automatically by the
  // pixel's built-in History State API listener (enabled by default in
  // index.html). It captures the correct per-route URL. We must NOT fire
  // PageView manually here: per Meta's docs you cannot fire more than one
  // PageView explicitly without that listener, and doing so alongside it
  // would double-count and report the wrong URL.

  useEffect(() => {
    const prevPathname = prevPathnameRef.current;
    prevPathnameRef.current = pathname;

    if (navigationType === "POP") {
      // Back/forward navigation — restore saved position if available
      const savedPos = sessionStorage.getItem(`scrollPos:${pathname}`);
      if (savedPos !== null) {
        // Double rAF ensures the page has painted before we scroll
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo({ top: parseInt(savedPos, 10), behavior: "instant" });
          });
        });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
    } else {
      // Forward navigation (PUSH/REPLACE) — save the departing page's position
      if (prevPathname !== pathname) {
        sessionStorage.setItem(`scrollPos:${prevPathname}`, window.scrollY);
      }
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    }
  }, [pathname, navigationType]);

  return null;
};

export default ScrollToTop;
